import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './css/NewPred.css';
import './css/Summary.css';
import labService from './labService';
import Navbar from './Navbar';
import HashLoader from "react-spinners/HashLoader";
import Visualization from './Visualization.js';
import authService from '../auth/authService';
import ClipLoader from "react-spinners/ClipLoader";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { getCoreColor } from './helpers/colorHelper';

const Summary = ({ isCollapsed, toggleSidebar }) => {
  const navigate = useNavigate();
  const { pk } = useParams();
  const [modelsList, setModelsList] = useState([]);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [loadPage, setloadPage] = useState(true);
  const [visualizationType, setVisualizationType] = useState('inconclusive');

  const [selectedId, setSelectedId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Stany dla Read-Across
  const [readAcrossData, setReadAcrossData] = useState(null);
  const [loadingRA, setLoadingRA] = useState(false);

  const toTitleCase = (str) => {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    );
  };

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('user'))?.access;
    if (!token) {
      setError("You must be logged in to view this page.");
      setLoading(false);
      return;
    }

    const fetchAnalysis = async () => {
      try {
        const response = await labService.getAnalysisDetail(pk, token);
        const data = response.data;
        setAnalysis(data);
        setVisualizationType(data.visualization_type || 'inconclusive');

        if (data.predictions && data.predictions.length > 0) {
          const donePreds = data.predictions.filter(p => p.status === 'Done');
          if (donePreds.length > 0) {
            setSelectedId(donePreds[0].id);
            const categories = [...new Set(donePreds.map(p => p.endpoint_category || 'Other'))];
            setExpandedCategories(categories);
          }
        }

        const ids = Object.keys(data.selected_models || {});
        if (ids.length > 0) {
          const response2 = await labService.getModelsArray(token, ids);
          setModelsList(response2.data);
        } else {
          setModelsList([]);
        }
      } catch (error) {
        console.error("Error fetching analysis:", error);
        setError("Failed to fetch analysis.");
      } finally {
        setloadPage(false);
      }
    };
    fetchAnalysis();
  }, [pk, navigate]);

  useEffect(() => {
    const fetchUserData = async () => {
      const userData = localStorage.getItem('user');
      if (!userData) { setLoading(false); return; }
      const parsedUserData = JSON.parse(userData);
      const token = parsedUserData.access;
      if (!token) { setLoading(false); return; }

      try {
        const response = await labService.getUserInfo(token);
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user data', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  const donePredictions = analysis ? analysis.predictions.filter(p => p.status === 'Done') : [];
  const activeResult = donePredictions.find(item => item.id === selectedId) || donePredictions[0];

  useEffect(() => {
    if (activeResult && activeResult.algorithm_type === 'READ_ACROSS_nQTb') {
      const token = currentUser?.access || JSON.parse(localStorage.getItem('user'))?.access;
      if (token) {
        setLoadingRA(true);
        labService.getReadAcrossData(activeResult.id, token)
          .then((response) => {
            setReadAcrossData(response.data);
          })
          .catch((error) => {
            console.error("Failed to fetch Read Across data:", error);
            setReadAcrossData(null);
          })
          .finally(() => {
            setLoadingRA(false);
          });
      }
    } else {
      setReadAcrossData(null);
    }
  }, [activeResult, currentUser]);


  if (loadPage || loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}><HashLoader color="#1B4480" size={80} /></div>;
  if (error) return <div style={{ marginTop: '100px', textAlign: 'center', color: 'red' }}>{error}</div>;
  if (!analysis) return null;


  const groupedEndpoints = donePredictions.reduce((acc, pred) => {
    const cat = pred.endpoint_category || 'Other';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(pred);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const closeSummary = () => {
    navigate('/laboratory');
  };

  const formatClassNotation = (predictionResult, classNotations) => {
    if (!classNotations) return predictionResult;
    try {
      const parsed = typeof classNotations === 'string' ? JSON.parse(classNotations.replace(/'/g, '"')) : classNotations;
      const key = String(predictionResult).trim();
      return parsed[key] || predictionResult;
    } catch (e) {
      return predictionResult;
    }
  };

  const getAssayName = (pred) => {
    return `${pred.model_name}: ${pred.cell_line_name}`;
  };

  const handleDownload = () => {
    if (currentUser && pk) {
      setIsDownloading(true);

      labService.getPredictionReport(pk, currentUser.access)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `prediction_report_${pk}.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch((error) => {
          console.error("Error downloading report:", error);
          setError("Failed to download report. Please try again.");
        })
        .finally(() => {
          setIsDownloading(false);
        });
    } else {
      setError("You must be logged in to download the report.");
    }
  };

  const openModal = () => setShowModal(true);

  const closeModal = () => {
    setShowModal(false);
    setIsConfirmed(false);
  };

  const handleDelete = () => {
    if (currentUser && pk && isConfirmed) {
      labService.deleteAnalysis(pk, currentUser.access)
        .then(() => {
          navigate('/laboratory/');
        })
        .catch(error => console.error(error))
        .finally(() => closeModal());
    }
  };

  const currentMeasureScale = analysis?.descriptors?.mean_particle_size || 0;

  return (
    <div className='lab-container'>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <div className="trash-icon">
                <FontAwesomeIcon icon={faTrashCan} size="2x" />
              </div>
              <button className="close-button" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className='modal-con'>
              <p className="p-delete">
                This action will delete all data regarding your prediction.
                <br />
                Your tokens <strong>will not be refunded</strong> in this case.
                <br />
                Please confirm by checking the box below.
              </p>

              <label className='p-delete confirm-txt'>
                <input
                  type="checkbox"
                  className='delete-check'
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                />
                I confirm that I want to delete this prediction result.
              </label>
              <div className="modal-buttons">
                <button className={`confirm-btn ${isConfirmed ? '' : 'disabled-btn'}`} onClick={handleDelete} disabled={!isConfirmed}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Navbar />
      <div className='main-wrapper'>
        <div className='models-version-details'>
          <header className="summary-header">
            <div className="pred-name-container">
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" color="#1B4480" cursor="pointer" height="1.5rem" width="1.5rem" xmlns="http://www.w3.org/2000/svg" onClick={closeSummary}>
                <path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M244 400L100 256l144-144M120 256h292" />
              </svg>
              <h2 className="pred-name">{analysis.name}</h2>
            </div>
            <div className="buttons-container">
              <button type="button" className="delete-button" onClick={openModal}>
                Delete prediction
              </button>
              <button
                type="button"
                className="download-button"
                onClick={handleDownload}
                disabled={isDownloading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minWidth: '180px', opacity: isDownloading ? 0.7 : 1, cursor: isDownloading ? 'wait' : 'pointer' }}
              >
                {isDownloading ? (
                  <>
                    <ClipLoader color={"#ffffff"} loading={true} size={15} />
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <span className="download-icon">
                      <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 1024 1024" height="1.5rem" width="1.5rem" xmlns="http://www.w3.org/2000/svg">
                        <path d="M505.7 661a8 8 0 0 0 12.6 0l112-141.7c4.1-5.2.4-12.9-6.3-12.9h-74.1V168c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v338.3H400c-6.7 0-10.4 7.7-6.3 12.9l112 141.8zM878 626h-60c-4.4 0-8 3.6-8 8v154H214V634c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v198c0 17.7 14.3 32 32 32h684c17.7 0 32-14.3 32-32V634c0-4.4-3.6-8-8-8z" />
                      </svg>
                    </span>
                    Download report
                  </>
                )}
              </button>
            </div>
          </header>

          {activeResult && !activeResult.in_domain && (
            <div role="alert" className="alert-box-container">
              <span className="alert-box-icon-container">
                <svg viewBox="0 0 24 24" focusable="false" className="alert-box-icon">
                  <path fill="currentColor" d="M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0Zm.25,5a1.5,1.5,0,1,1-1.5,1.5A1.5,1.5,0,0,1,12.25,5ZM14.5,18.5h-4a1,1,0,0,1,0-2h.75a.25.25,0,0,0,.25-.25v-4.5a.25.25,0,0,0-.25-.25H10.5a1,1,0,0,1,0-2h1a2,2,0,0,1,2,2v4.75a.25.25,0,0,0,.25.25h.75a1,1,0,1,1,0,2Z" />
                </svg>
              </span>
              <div className="alert-box-text">
                Your prediction was successful, however due to being out of the domain of applicability of the model, the estimation may be burdened with a larger error. License that was used lets you perform additional prediction until it resides within model’s applicability domain.
              </div>
            </div>
          )}

          <div className='summary-main-content-container' style={{
            display: 'flex',
            gap: '25px',
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>

            {/* KOLUMNA 1: Endpoints */}
            <div className="section-container" style={{ flex: '1 1 320px', maxWidth: '390px', display: 'flex', flexDirection: 'column', height: '1200px' }}>
              <div className="section-inner" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, boxSizing: 'border-box' }}>
                <h3 className="section-title">Endpoints</h3>

                <div
                  className="endpoints-wrapper"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    paddingRight: '10px'
                  }}
                >
                  {Object.entries(groupedEndpoints).map(([category, endpoints]) => {
                    const isExpanded = expandedCategories.includes(category);
                    if (endpoints.length === 0 && category === "Other") return null;

                    return (
                      <div key={category} className="category-group">
                        <div
                          onClick={() => toggleCategory(category)}
                          className='category-expand-container'
                        >
                          <h4 className="category-label" style={{ flexGrow: 1, textTransform: 'capitalize' }}>
                            <span className={`expand-icon ${!isExpanded ? 'collapsed' : ''}`}>
                              ▼
                            </span>
                            {category}
                          </h4>
                        </div>

                        {isExpanded && endpoints.map((ep) => (
                          <div
                            key={ep.id}
                            className={`endpoint-item ${selectedId === ep.id ? 'active-endpoint' : ''}`}
                            onClick={() => setSelectedId(ep.id)}
                            style={{ padding: '1px 12px' }} // ZMNIEJSZONY PADDING TUTAJ
                          >
                            <div className={`description-container ${selectedId === ep.id ? 'description-container-active' : ''}`}>
                              {/* ZMNIEJSZONY LINE-HEIGHT TUTAJ (1.2 zamiast 1.4) */}
                              <p className="assay-details" style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', lineHeight: '1.2', margin: 0 }}>
                                {getAssayName(ep)}
                              </p>
                            </div>
                          </div>
                        ))}

                        {isExpanded && endpoints.length === 0 && (
                          <p className="no-endpoints-msg">No endpoints</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* KOLUMNA 2: Details */}
            {activeResult && (
              <div className="details-grid-container" style={{
                flex: '1 1 300px',
                minWidth: '280px',
                display: 'flex',
                flexDirection: 'column',
                gap: '25px',
                height: '1200px'
              }}>

                <div className="endpoint-panel" style={{ flexShrink: 0 }}>
                  <div className="endpoint-content">
                    <h3 className="chakra-heading panel-heading">Endpoint details</h3>
                    <div className="endpoint-info-list">
                      <p className="assay-description">{getAssayName(activeResult)}</p>
                      <p className="variable-label">Dependent variable</p>
                      <p className="variable-value">{Object.keys(activeResult.calc_param || {})[0] || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="prediction-panel" style={{ flexShrink: 0 }}>
                  <h3 className="chakra-heading panel-heading-alt">Prediction details</h3>
                  {!activeResult.in_domain ? (
                    <span className="status-badge-out-of-domain">Prediction is out of domain</span>
                  ) : (
                    <span className="status-badge-in-domain">Prediction is within domain</span>
                  )}
                  <div className="prediction-data-box">
                    <p className="prediction-label">Predicted value</p>
                    <p className="prediction-result">
                      {formatClassNotation(activeResult.prediction_result, activeResult.class_notations)}
                    </p>
                  </div>
                </div>

                <div className="descriptiors-panel" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0
                }}>
                  <h3 className="chakra-heading panel-heading">Descriptor values</h3>

                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '5px' }}>
                    {activeResult.report_data?.descriptors_to_model && Object.entries(activeResult.report_data.descriptors_to_model).map(([key, value], index) => (
                      <div key={`desc-${index}`} className="prediction-data-box">
                        <p className="prediction-label" style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</p>
                        <p className="prediction-result">{value}</p>
                      </div>
                    ))}

                    {activeResult.report_data?.cell_line_descr && activeResult.report_data.cell_line_descr.map((item, idx) => (
                      <div key={`cell-${idx}`} className="prediction-data-box">
                        <p className="prediction-label">{item.Label}</p>
                        <p className="prediction-result">{item.Value}</p>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}

            {/* KOLUMNA 3: Nanoform Characteristic */}
            <div className="nanoform-container" style={{ flex: '1.5 1 400px', display: 'flex', flexDirection: 'column', height: '1200px' }}>
              <div className="nanoform-card" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, boxSizing: 'border-box' }}>
                <h3 className="section-title">Nanoform characteristic</h3>

                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '30px', paddingRight: '5px' }}>

                  <div className="image-preview-wrapper" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <div className="nanoform-image" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'transparent', border: 'none', width: '100%' }}>
                      <Visualization
                        core={{ name: analysis.endpoint_type_name }}
                        coreModification={analysis.core_modification || null}
                        coreColor={getCoreColor(analysis.endpoint_type_name)}
                        surfaceFunctionalization={!!analysis.surface_functionalization}
                        surfaceFunctionalizationType={analysis.surface_functionalization || null}
                        surfaceFunctionalizationColor="#9ed2ff"
                        lineColor="transparent"
                        isNanoformImageGenerated={false}
                        visualizationType={visualizationType}
                        measureScale={currentMeasureScale}
                        width={400}
                        height={300}
                        staticImage={false}
                      />
                    </div>
                  </div>

                  <div className="specs-list-container" style={{ width: '100%' }}>
                    <div className="prediction-data-box">
                      <p className="spec-header-label">Core</p>
                      <div className="spec-icon-wrapper">
                        <div className="spec-icon-flex">
                          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" color={getCoreColor(analysis.endpoint_type_name)} height="2rem" width="2rem" xmlns="http://www.w3.org/2000/svg">
                            <path d="M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z" />
                          </svg>
                          {analysis.endpoint_type_name}
                        </div>
                      </div>
                    </div>

                    {analysis.selected_crystal_structure && (
                      <div className="prediction-data-box">
                        <p className="spec-property-label">Crystal Structure</p>
                        <p className="prediction-result">{analysis.selected_crystal_structure}</p>
                      </div>
                    )}

                    {analysis.core_modification && (
                      <div className="prediction-data-box">
                        <p className="spec-property-label">Core Modification</p>
                        <p className="prediction-result">{analysis.core_modification}</p>
                      </div>
                    )}

                    {analysis.surface_functionalization && (
                      <div className="prediction-data-box">
                        <p className="spec-property-label">Surface Functionalization</p>
                        <p className="prediction-result">{analysis.surface_functionalization}</p>
                      </div>
                    )}

                    {analysis.descriptors && Object.entries(analysis.descriptors).map(([key, value], idx) => (
                      <div className="prediction-data-box" key={`nano-${idx}`}>
                        <p className="spec-property-label" style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</p>
                        <p className="prediction-result">{value}</p>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>

          </div>

          {/* ---- UJEDNOLICONA TABELA READ-ACROSS ---- */}
          {activeResult && activeResult.algorithm_type === 'READ_ACROSS_nQTb' && (
            <div className="read-across-section" style={{ marginTop: '40px', width: '100%' }}>
              <h3 className="section-title" style={{ textAlign: 'center', width: '100%', marginBottom: '20px', color: '#1B4480' }}>
                Source Nanoforms (Read-Across)
              </h3>

              {loadingRA ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                  <ClipLoader color={"#1B4480"} loading={true} size={30} />
                </div>
              ) : readAcrossData && readAcrossData.table_physchem && readAcrossData.table_physchem.length > 0 ? (
                <div className="table-container" style={{ marginTop: '20px', overflowX: 'auto', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', padding: '20px' }}>
                  <table className="license-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {Object.keys(readAcrossData.table_physchem[0]).map((key, idx) => (
                          <th key={`th-${idx}`} style={{ textAlign: 'left', textTransform: 'none', padding: '12px', borderBottom: '2px solid #e2e8f0', color: '#1B4480' }}>
                            {toTitleCase(key)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {readAcrossData.table_physchem.map((row, rowIdx) => (
                        <tr key={`tr-${rowIdx}`} style={{ borderBottom: '1px solid #edf2f7' }}>
                          {Object.values(row).map((val, valIdx) => (
                            <td key={`td-${rowIdx}-${valIdx}`} style={{ padding: '12px', color: '#1B4480' }}>
                              {val || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-placeholder" style={{ marginTop: '20px' }}>
                  No source data available for this prediction.
                </div>
              )}
            </div>
          )}
          {/* ---- KONIEC TABELI READ-ACROSS ---- */}

        </div>
      </div>
    </div>
  );
};

export default Summary;
